import type { BackendAIResult } from '@services/menuUploaderV2Service';
import type {
  DetectedCategory,
  DetectedExtra,
  DetectedProduct,
} from '@pages/Menu/Products/MenuUploaderV2/types/menuUpload.types';

import { PRICE_TOLERANCE_RATIO } from '../constants';
import type { ConsolidatedAnalysis, DuplicateGroup, UploadQueueItem } from '../types';
import { isPriceClose, normalize } from './normalize';

type BackendCategory = BackendAIResult['categories'][number];
type BackendProduct = BackendAIResult['products'][number];
type BackendExtra = BackendAIResult['extras'][number];

const buildPrefixedId = (fileId: string, originalId: string): string => `${fileId}__${originalId}`;

const mapBackendCategory = (source: BackendCategory, newId: string): DetectedCategory => ({
  id: newId,
  type: 'category',
  name: source.nombre,
  confidence: source.confidence,
  originalText: source.originalText,
});

const mapBackendProduct = (source: BackendProduct, newId: string, newCategoryId: string): DetectedProduct => {
  const firstPrice = source.opciones?.[0]?.precio;
  return {
    id: newId,
    type: 'product',
    name: source.nombre,
    confidence: source.confidence,
    originalText: source.originalText,
    description: source.descripcion,
    category: newCategoryId,
    price: typeof firstPrice === 'number' ? firstPrice : undefined,
    // Presentaciones COMPLETAS (ej. "3 pzas $100 / 6 pzas $150"): el persist
    // las publica literales cuando hay más de una. `price` queda como precio
    // principal (la primera) para listas, dedup y retrocompat V3. Antes este
    // mapper tiraba el array y el menú publicado perdía las presentaciones.
    opciones: source.opciones?.map((op) => ({
      presentacion: op.presentacion,
      precio: op.precio,
      costo_produccion: typeof op.costo_produccion === 'number' ? op.costo_produccion : 0,
      estatus: 'activo' as const,
    })),
  };
};

const mapBackendExtra = (source: BackendExtra, newId: string): DetectedExtra => ({
  id: newId,
  type: 'extra',
  name: source.nombre,
  confidence: source.confidence,
  originalText: source.originalText,
  price: source.precio,
});

interface FileScopedData {
  fileId: string;
  categories: DetectedCategory[];
  products: DetectedProduct[];
  extras: BackendExtra[];
  rawExtras: Array<{ remapped: DetectedExtra; original: BackendExtra }>;
  warnings: string[];
}

const collectFromItem = (item: UploadQueueItem): FileScopedData | null => {
  if (item.status !== 'completed' || !item.result) return null;
  const fileId = item.id;
  const result = item.result;

  // Reasignar IDs locales con prefijo de fileId para evitar colisiones.
  const categoryIdMap = new Map<string, string>();
  const categories: DetectedCategory[] = result.categories.map((cat) => {
    const newId = buildPrefixedId(fileId, cat.id);
    categoryIdMap.set(cat.id, newId);
    return mapBackendCategory(cat, newId);
  });

  const products: DetectedProduct[] = result.products.map((prod) => {
    const newId = buildPrefixedId(fileId, prod.id);
    const remappedCategoryId = categoryIdMap.get(prod.categoryId) ?? buildPrefixedId(fileId, prod.categoryId);
    return mapBackendProduct(prod, newId, remappedCategoryId);
  });

  const rawExtras = result.extras.map((extra) => {
    const newId = buildPrefixedId(fileId, extra.id);
    return { remapped: mapBackendExtra(extra, newId), original: extra };
  });

  const warnings: string[] = [];
  if (result.wasTruncated) warnings.push('Respuesta del modelo truncada.');
  if (result.suspiciouslyLowCount) warnings.push('Cantidad de productos sospechosamente baja.');
  if (Array.isArray(result.suggestions)) {
    warnings.push(...result.suggestions);
  }

  return {
    fileId,
    categories,
    products,
    extras: rawExtras.map((e) => e.original),
    rawExtras,
    warnings,
  };
};

interface DedupeResult<T> {
  kept: T[];
  /** Map oldId -> representativeId for entries that were merged into a representative. */
  remap: Map<string, string>;
  duplicateGroups: DuplicateGroup[];
}

const dedupeCategories = (categories: DetectedCategory[]): DedupeResult<DetectedCategory> => {
  const kept: DetectedCategory[] = [];
  const remap = new Map<string, string>();
  const groupsByRep = new Map<string, DuplicateGroup>();
  const repByKey = new Map<string, DetectedCategory>();

  for (const cat of categories) {
    const key = normalize(cat.name);
    const rep = repByKey.get(key);
    if (!rep) {
      repByKey.set(key, cat);
      kept.push(cat);
      remap.set(cat.id, cat.id);
      continue;
    }
    remap.set(cat.id, rep.id);
    const existingGroup = groupsByRep.get(rep.id);
    if (existingGroup) {
      existingGroup.candidateIds.push(cat.id);
    } else {
      groupsByRep.set(rep.id, {
        type: 'category',
        representativeId: rep.id,
        candidateIds: [cat.id],
        reason: 'name_exact',
        decision: 'keep_representative',
      });
    }
  }

  return { kept, remap, duplicateGroups: Array.from(groupsByRep.values()) };
};

const dedupeProducts = (products: DetectedProduct[]): DedupeResult<DetectedProduct> => {
  const kept: DetectedProduct[] = [];
  const remap = new Map<string, string>();
  const groupsByRep = new Map<string, DuplicateGroup>();
  // Para cada nombre normalizado mantenemos lista de representantes
  // (puede haber múltiples si son variantes con precios distintos).
  const repsByName = new Map<string, DetectedProduct[]>();

  for (const prod of products) {
    const key = normalize(prod.name);
    const reps = repsByName.get(key) ?? [];
    let matched: DetectedProduct | null = null;
    for (const rep of reps) {
      const repPrice = typeof rep.price === 'number' ? rep.price : 0;
      const prodPrice = typeof prod.price === 'number' ? prod.price : 0;
      if (isPriceClose(repPrice, prodPrice, PRICE_TOLERANCE_RATIO)) {
        matched = rep;
        break;
      }
    }
    if (!matched) {
      reps.push(prod);
      repsByName.set(key, reps);
      kept.push(prod);
      remap.set(prod.id, prod.id);
      continue;
    }
    remap.set(prod.id, matched.id);
    const existingGroup = groupsByRep.get(matched.id);
    if (existingGroup) {
      existingGroup.candidateIds.push(prod.id);
    } else {
      groupsByRep.set(matched.id, {
        type: 'product',
        representativeId: matched.id,
        candidateIds: [prod.id],
        reason: 'name_and_price_close',
        decision: 'keep_representative',
      });
    }
  }

  return { kept, remap, duplicateGroups: Array.from(groupsByRep.values()) };
};

const dedupeExtras = (extras: DetectedExtra[]): DedupeResult<DetectedExtra> => {
  const kept: DetectedExtra[] = [];
  const remap = new Map<string, string>();
  const groupsByRep = new Map<string, DuplicateGroup>();
  const repsByName = new Map<string, DetectedExtra[]>();

  for (const extra of extras) {
    const key = normalize(extra.name);
    const reps = repsByName.get(key) ?? [];
    let matched: DetectedExtra | null = null;
    for (const rep of reps) {
      const repPrice = typeof rep.price === 'number' ? rep.price : 0;
      const extraPrice = typeof extra.price === 'number' ? extra.price : 0;
      if (isPriceClose(repPrice, extraPrice, PRICE_TOLERANCE_RATIO)) {
        matched = rep;
        break;
      }
    }
    if (!matched) {
      reps.push(extra);
      repsByName.set(key, reps);
      kept.push(extra);
      remap.set(extra.id, extra.id);
      continue;
    }
    remap.set(extra.id, matched.id);
    const existingGroup = groupsByRep.get(matched.id);
    if (existingGroup) {
      existingGroup.candidateIds.push(extra.id);
    } else {
      groupsByRep.set(matched.id, {
        type: 'extra',
        representativeId: matched.id,
        candidateIds: [extra.id],
        reason: 'name_and_price_close',
        decision: 'keep_representative',
      });
    }
  }

  return { kept, remap, duplicateGroups: Array.from(groupsByRep.values()) };
};

export const consolidate = (items: UploadQueueItem[]): ConsolidatedAnalysis => {
  const perFileWarnings: Record<string, string[]> = {};
  let wasTruncated = false;
  let suspiciouslyLowCount = false;

  const fileScoped: FileScopedData[] = [];
  for (const item of items) {
    const data = collectFromItem(item);
    if (!data) continue;
    fileScoped.push(data);
    perFileWarnings[data.fileId] = data.warnings;
    if (item.result?.wasTruncated) wasTruncated = true;
    if (item.result?.suspiciouslyLowCount) suspiciouslyLowCount = true;
  }

  // Tracking de origen elemento -> [fileIds]
  const sourceByElementId = new Map<string, Set<string>>();
  const addSource = (elementId: string, fileId: string) => {
    const set = sourceByElementId.get(elementId) ?? new Set<string>();
    set.add(fileId);
    sourceByElementId.set(elementId, set);
  };

  const allCategories: DetectedCategory[] = [];
  const allProducts: DetectedProduct[] = [];
  const allExtras: DetectedExtra[] = [];

  for (const data of fileScoped) {
    for (const cat of data.categories) {
      allCategories.push(cat);
      addSource(cat.id, data.fileId);
    }
    for (const prod of data.products) {
      allProducts.push(prod);
      addSource(prod.id, data.fileId);
    }
    for (const { remapped } of data.rawExtras) {
      allExtras.push(remapped);
      addSource(remapped.id, data.fileId);
    }
  }

  const catDedup = dedupeCategories(allCategories);
  // Reasignar categoryId en productos al representante.
  const productsWithRemappedCats = allProducts.map((prod) => {
    if (!prod.category) return prod;
    const repCatId = catDedup.remap.get(prod.category) ?? prod.category;
    if (repCatId === prod.category) return prod;
    return { ...prod, category: repCatId };
  });

  const prodDedup = dedupeProducts(productsWithRemappedCats);
  const extraDedup = dedupeExtras(allExtras);

  // Construir sourceMap final sólo para elementos kept; mergeamos fuentes de duplicados al representante.
  const finalSourceMap: Record<string, string[]> = {};

  const mergeSourcesInto = (keptIds: string[], remap: Map<string, string>, originals: Array<{ id: string }>) => {
    const merged = new Map<string, Set<string>>();
    for (const el of originals) {
      const repId = remap.get(el.id) ?? el.id;
      if (!keptIds.includes(repId)) continue;
      const sources = sourceByElementId.get(el.id);
      if (!sources) continue;
      const acc = merged.get(repId) ?? new Set<string>();
      for (const s of sources) acc.add(s);
      merged.set(repId, acc);
    }
    for (const [id, set] of merged) {
      finalSourceMap[id] = Array.from(set);
    }
  };

  mergeSourcesInto(
    catDedup.kept.map((c) => c.id),
    catDedup.remap,
    allCategories,
  );
  mergeSourcesInto(
    prodDedup.kept.map((p) => p.id),
    prodDedup.remap,
    productsWithRemappedCats,
  );
  mergeSourcesInto(
    extraDedup.kept.map((e) => e.id),
    extraDedup.remap,
    allExtras,
  );

  // Serializamos el `extraDedup.remap` (Map) a `Record<string,string>` para que
  // el consumidor (SmartMenuOnboarding) pueda remapear `section.opciones` y
  // evitar refs colgadas a extras deduplicados. Ver `ConsolidatedAnalysis.extrasRemap`.
  const extrasRemap: Record<string, string> = {};
  for (const [oldId, repId] of extraDedup.remap) {
    extrasRemap[oldId] = repId;
  }

  // Snapshot pre-dedupe por ID. Los `allCategories/allProducts/allExtras` ya
  // tienen IDs prefijados con `${fileId}__`. Incluye representantes (que también
  // están en `kept`) y candidatos descartados (que NO están en `kept`).
  // Cubre Seer bug 3378651276: DuplicateBanner necesita resolver el nombre/precio
  // de los candidates, y `applyDuplicateDecisionsToAnalysis` los re-introduce
  // cuando el usuario elige `keep_all`.
  const preDedupeCategoriesById: Record<string, DetectedCategory> = {};
  for (const c of allCategories) preDedupeCategoriesById[c.id] = c;
  const preDedupeProductsById: Record<string, DetectedProduct> = {};
  for (const p of productsWithRemappedCats) preDedupeProductsById[p.id] = p;
  const preDedupeExtrasById: Record<string, DetectedExtra> = {};
  for (const e of allExtras) preDedupeExtrasById[e.id] = e;

  return {
    categories: catDedup.kept,
    products: prodDedup.kept,
    extras: extraDedup.kept,
    // V4: sections vacías en V3 (legacy). El adapter transformV4toFrontend las
    // poblará cuando el backend emita schemaVersion === '4.0'.
    sections: [],
    duplicateGroups: [...catDedup.duplicateGroups, ...prodDedup.duplicateGroups, ...extraDedup.duplicateGroups],
    sourceMap: finalSourceMap,
    warnings: {
      wasTruncated,
      suspiciouslyLowCount,
      perFile: perFileWarnings,
    },
    extrasRemap,
    preDedupeCategoriesById,
    preDedupeProductsById,
    preDedupeExtrasById,
  };
};
